// Flattens whatever the Incode SDK 2.0 threw into Datadog-friendly fields, so
// INCODE_SDK_ERROR carries the real failure instead of just a message string.
// Verified against @incodetech/core@2.2.0: API failures are `IncodeApiError`
// (endpoint, status = Incode body code, httpStatus) with the raw
// `FetchHttpError` (url, method, status, statusText, data) on `cause`.
//
// Never emits a key literally named `status` — it overwrites the Datadog log
// severity (see report-event.helper.ts). Incode's code goes out as
// `incodeApiStatus` instead.

const MAX_FIELD_LENGTH = 2000;

const truncate = (value: string) => (
  value.length > MAX_FIELD_LENGTH ? `${value.slice(0, MAX_FIELD_LENGTH)}…` : value
);

const stringify = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') return truncate(value);
  try {
    return truncate(JSON.stringify(value));
  } catch {
    return truncate(String(value));
  }
};

const pick = (source: unknown, key: string): unknown => (
  typeof source === 'object' && source !== null ? (source as Record<string, unknown>)[key] : undefined
);

export type IncodeErrorDetails = {
  errorMessage: string;
  errorName?: string;
  errorStack?: string;
  incodeEndpoint?: string;
  incodeApiStatus?: number;
  httpStatus?: number;
  httpStatusText?: string;
  httpMethod?: string;
  requestUrl?: string;
  responseBody?: string;
  errorCause?: string;
};

export function describeIncodeError(error: unknown, fallbackMessage: string): IncodeErrorDetails {
  if (!(error instanceof Error)) {
    // Non-Error throws (strings, plain objects) used to collapse into the
    // fallback message — keep whatever was thrown.
    return { errorMessage: stringify(error) ?? fallbackMessage };
  }

  const cause = (error as { cause?: unknown }).cause;
  const endpoint = pick(error, 'endpoint');
  const apiStatus = pick(error, 'status');
  const httpStatus = pick(error, 'httpStatus') ?? pick(cause, 'status');
  const statusText = pick(cause, 'statusText');
  const method = pick(cause, 'method');
  const url = pick(cause, 'url');

  const details: IncodeErrorDetails = {
    errorMessage: error.message || fallbackMessage,
    errorName: error.name,
    errorStack: error.stack ? truncate(error.stack) : undefined,
    incodeEndpoint: typeof endpoint === 'string' ? endpoint : undefined,
    incodeApiStatus: typeof apiStatus === 'number' ? apiStatus : undefined,
    httpStatus: typeof httpStatus === 'number' ? httpStatus : undefined,
    httpStatusText: typeof statusText === 'string' ? statusText : undefined,
    httpMethod: typeof method === 'string' ? method : undefined,
    requestUrl: typeof url === 'string' ? url : undefined,
    responseBody: stringify(pick(cause, 'data')),
  };

  if (cause !== undefined && details.responseBody === undefined) {
    details.errorCause = cause instanceof Error ? truncate(`${cause.name}: ${cause.message}`) : stringify(cause);
  }

  return details;
}

// Datadog's log list and RUM error list show only the message, so put the
// real failure in it. Keeps the INCODE_SDK_ERROR prefix so existing
// free-text searches still match; monitors should key on @event.
export function incodeErrorTitle(phase: string, details: { errorMessage?: string; moduleErrorCode?: string | null; incodeApiStatus?: number; httpStatus?: number }) {
  const code = details.moduleErrorCode
    ?? (details.incodeApiStatus !== undefined ? `incode ${details.incodeApiStatus}` : undefined)
    ?? (details.httpStatus !== undefined ? `HTTP ${details.httpStatus}` : undefined);
  return `INCODE_SDK_ERROR [${phase}]${code ? ` ${code}` : ''}: ${details.errorMessage ?? 'unknown error'}`;
}

// Network-level failures loading the SDK, its lazy modules or WASM — Safari
// says "Load failed" / "Importing a module script failed.", Chrome "Failed
// to fetch" / "Failed to fetch dynamically imported module". API errors with
// a real response (IncodeApiError) are not transient by this definition.
const TRANSIENT_LOAD_MESSAGE = /load failed|importing a module script failed|failed to fetch|dynamically imported module|networkerror/i;

export function isTransientLoadError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  return error instanceof Error && error.name !== 'IncodeApiError' && TRANSIENT_LOAD_MESSAGE.test(error.message);
}
